import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { EASE } from "@/components/aklive/motion";
import { Logo } from "@/components/aklive/Logo";

const LINKS = [
  { to: "/ethos", label: "Ethos" },
  { to: "/services", label: "Services" },
  { to: "/work", label: "Work" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const reduce = useReducedMotion();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
        scrolled || open ? "border-line bg-paper/85 backdrop-blur-md" : "border-transparent bg-transparent"
      }`}
    >
      <a
        href="#main"
        data-testid="skip-to-content-link"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:bg-ink focus:px-4 focus:py-2 focus:text-paper"
      >
        Skip to content
      </a>
      <div
        className={`container-ak flex items-center justify-between transition-[height] duration-300 ${
          scrolled ? "h-14" : "h-16 md:h-20"
        }`}
      >
        <Logo onClick={() => setOpen(false)} />

        <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}-link`}
              className={({ isActive }) =>
                `link-underline text-sm tracking-wide ${isActive ? "text-ink" : "text-ink2 hover:text-ink"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/contact" data-testid="header-start-project-btn" className="btn-primary h-10 px-5 text-sm">
            Start a Project
          </Link>
        </nav>

        <button
          type="button"
          data-testid="mobile-menu-btn"
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen(!open)}
          className="flex h-11 w-11 flex-col items-center justify-center gap-[6px] md:hidden"
        >
          <span
            className={`block h-[2px] w-6 bg-ink transition-transform duration-300 ${open ? "translate-y-[4px] rotate-45" : ""}`}
          />
          <span
            className={`block h-[2px] w-6 bg-ink transition-transform duration-300 ${open ? "-translate-y-[4px] -rotate-45" : ""}`}
          />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="fixed inset-0 z-[-1] flex h-[100dvh] flex-col bg-paper pt-24 md:hidden"
          >
            <nav className="container-ak flex flex-col gap-2" aria-label="Mobile">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={reduce ? { opacity: 0 } : { opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.08 + i * 0.06, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}-link`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-baseline gap-4 border-b border-line py-5 font-display text-4xl font-bold tracking-tight ${
                        isActive ? "text-ink" : "text-ink2"
                      }`
                    }
                  >
                    <span className="font-mono text-xs tracking-[0.2em] text-ember">0{i + 1}</span>
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.36, ease: EASE }}
                className="pt-8"
              >
                <Link
                  to="/contact"
                  data-testid="mobile-start-project-btn"
                  onClick={() => setOpen(false)}
                  className="btn-primary h-14 w-full text-base"
                >
                  Start a Project
                </Link>
              </motion.div>
            </nav>
            <p className="container-ak mt-auto pb-8 font-mono text-[10px] uppercase tracking-[0.2em] text-ink2">
              Built for what comes next
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
